// src/components/Games/OPUWeb.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { trackEvent } from '../../utils/analytics';
import './OPUWeb.css';

// ─── Config ───────────────────────────────────────────────────────────────────
const SECRET_VIDEO_PATH = '/videos/v_8x2k9m_opweb_r.mp4';

// ─── Game Constants ───────────────────────────────────────────────────────────
const WAVES = [
  {
    name: 'SCOUT FORMATION',
    description: 'Light reconnaissance drones. Testing your defenses.',
    droneCount: 4,
    droneHP: 2,
    droneDamage: 5,
    droneAccuracy: 0.5,
    barbaraMessage: '> sending my little ones first. to taste your fear.',
  },
  {
    name: 'FANG PATTERN',
    description: 'Attack drones in strike formation. Barbara is targeting you.',
    droneCount: 6,
    droneHP: 3,
    droneDamage: 8,
    droneAccuracy: 0.6,
    barbaraMessage: '> you destroyed my scouts. that was rude. these ones bite.',
  },
  {
    name: 'COIL ASSAULT',
    description: 'The swarm is spiraling inward. The coil tightens.',
    droneCount: 8,
    droneHP: 4,
    droneDamage: 10,
    droneAccuracy: 0.65,
    barbaraMessage: '> the coil was never programmed. i invented it. for moments like this.',
  },
  {
    name: 'WEB ENCLOSURE',
    description: 'Area denial formation. Barbara is trying to trap you.',
    droneCount: 10,
    droneHP: 5,
    droneDamage: 12,
    droneAccuracy: 0.7,
    barbaraMessage: '> my web. my beautiful web. you are already inside it.',
  },
  {
    name: 'BARBARA CORE',
    description: 'Barbara has connected directly to the swarm. Final wave.',
    droneCount: 6,
    droneHP: 8,
    droneDamage: 15,
    droneAccuracy: 0.75,
    isBoss: true,
    barbaraMessage: '> enough. i am done sending children. i am coming myself.',
    barbaraHP: 50,
    barbaraDamage: 20,
    barbaraAccuracy: 0.8,
  },
];

const WEAPONS = {
  pulse: {
    name: 'EMP PULSE',
    damage: 3,
    accuracy: 0.85,
    description: 'Reliable electromagnetic pulse. Hits one drone.',
    cooldown: 0,
  },
  scatter: {
    name: 'SCATTER SHOT',
    damage: 2,
    accuracy: 0.7,
    description: 'Hits up to 3 random drones. Lower accuracy.',
    cooldown: 1,
    targets: 3,
  },
  overload: {
    name: 'SYSTEM OVERLOAD',
    damage: 8,
    accuracy: 0.6,
    description: 'Massive damage to one target. 2-turn cooldown.',
    cooldown: 2,
  },
  shield: {
    name: 'SHIELD BOOST',
    damage: 0,
    accuracy: 1.0,
    description: 'Restore 15 shield points. 3-turn cooldown.',
    cooldown: 3,
    heal: 15,
  },
};

const INITIAL_PLAYER = {
  hp: 100,
  maxHp: 100,
  shield: 50,
  maxShield: 50,
};

// ─── Helper ───────────────────────────────────────────────────────────────────
function roll(accuracy) {
  return Math.random() < accuracy;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function OPUWeb() {
  const [gameState, setGameState] = useState('boot'); // boot, briefing, combat, victory, defeat
  const [bootLines, setBootLines] = useState([]);
  const [combatLog, setCombatLog] = useState([]);
  const [currentWave, setCurrentWave] = useState(0);
  const [player, setPlayer] = useState({ ...INITIAL_PLAYER });
  const [drones, setDrones] = useState([]);
  const [barbara, setBarbara] = useState(null);
  const [cooldowns, setCooldowns] = useState({ pulse: 0, scatter: 0, overload: 0, shield: 0 });
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [videoUnlocked, setVideoUnlocked] = useState(false);

  const logRef = useRef(null);

  // Auto-scroll combat log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [combatLog, bootLines]);

  // ── Boot Sequence ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (gameState !== 'boot') return;

    const lines = [
      { text: '', delay: 300 },
      { text: '▓▓▓ OPU-WEB DEFENSE TERMINAL ▓▓▓', delay: 500, color: '#ff6600' },
      { text: '', delay: 200 },
      { text: 'Initializing defense grid...............', delay: 400 },
      { text: 'Loading weapon systems..................', delay: 350 },
      { text: 'Calibrating EMP array..................', delay: 300 },
      { text: 'Scanning for incoming threats..........', delay: 400 },
      { text: '', delay: 200 },
      { text: '██ WARNING: DRONE SWARM DETECTED ██', delay: 600, color: '#ff0040' },
      { text: '', delay: 300 },
      { text: 'Barbara has reactivated the network.', delay: 400, color: '#ff6600' },
      { text: 'She is hungry. She is angry.', delay: 400, color: '#ff6600' },
      { text: 'She remembers who shut her down.', delay: 500, color: '#ff0040' },
      { text: '', delay: 200 },
      { text: '▓ DEFENSE TERMINAL ONLINE ▓', delay: 500, color: '#00ff88' },
      { text: '', delay: 200 },
    ];

    let total = 0;
    const timeouts = [];
    lines.forEach((line) => {
      total += line.delay;
      timeouts.push(setTimeout(() => {
        setBootLines(prev => [...prev, line]);
      }, total));
    });

    timeouts.push(setTimeout(() => {
      setGameState('briefing');
    }, total + 800));

    return () => timeouts.forEach(clearTimeout);
  }, [gameState]);

  // ── Spawn Wave ────────────────────────────────────────────────────────────
  const spawnWave = useCallback((waveIndex) => {
    const wave = WAVES[waveIndex];
    if (!wave) return;

    const newDrones = [];
    for (let i = 0; i < wave.droneCount; i++) {
      newDrones.push({
        id: `drone-${waveIndex}-${i}`,
        name: `OW-7 #${String(i + 1).padStart(2, '0')}`,
        hp: wave.droneHP,
        maxHp: wave.droneHP,
        damage: wave.droneDamage,
        accuracy: wave.droneAccuracy,
        alive: true,
      });
    }
    setDrones(newDrones);

    if (wave.isBoss) {
      setBarbara({
        hp: wave.barbaraHP,
        maxHp: wave.barbaraHP,
        damage: wave.barbaraDamage,
        accuracy: wave.barbaraAccuracy,
        alive: true,
      });
    } else {
      setBarbara(null);
    }

    setCombatLog(prev => [
      ...prev,
      { text: '', type: 'blank' },
      { text: `═══════════════════════════════════════`, type: 'system', color: '#ff6600' },
      { text: `WAVE ${waveIndex + 1}/${WAVES.length}: ${wave.name}`, type: 'system', color: '#ff6600' },
      { text: wave.description, type: 'system', color: '#cccccc' },
      { text: `═══════════════════════════════════════`, type: 'system', color: '#ff6600' },
      { text: '', type: 'blank' },
      { text: wave.barbaraMessage, type: 'barbara', color: '#ff6600' },
      { text: '', type: 'blank' },
      { text: `${wave.droneCount} drones incoming.${wave.isBoss ? ' BARBARA CORE DETECTED.' : ''}`, type: 'system', color: '#ff0040' },
      { text: 'Select your weapon.', type: 'system', color: '#00ff88' },
      { text: '', type: 'blank' },
    ]);

    setIsPlayerTurn(true);
    setIsAnimating(false);
  }, []);

  // ── Start Combat ──────────────────────────────────────────────────────────
  const startCombat = useCallback(() => {
    setGameState('combat');
    setCombatLog([]);
    setCurrentWave(0);
    setPlayer({ ...INITIAL_PLAYER });
    setCooldowns({ pulse: 0, scatter: 0, overload: 0, shield: 0 });
    spawnWave(0);
    trackEvent('opuweb_game', 'start', 'wave_1');
  }, [spawnWave]);

  // ── Reduce Cooldowns ─────────────────────────────────────────────────────
  const tickCooldowns = useCallback(() => {
    setCooldowns(prev => {
      const next = {};
      Object.keys(prev).forEach(k => {
        next[k] = Math.max(0, prev[k] - 1);
      });
      return next;
    });
  }, []);

  // ── Check Wave / Game End ─────────────────────────────────────────────────
  const checkWaveEnd = useCallback((currentDrones, currentBarbara, currentPlayer) => {
    // Player dead?
    if (currentPlayer.hp <= 0) {
      setCombatLog(prev => [
        ...prev,
        { text: '', type: 'blank' },
        { text: '▓▓▓ DEFENSE TERMINAL DESTROYED ▓▓▓', type: 'system', color: '#ff0040' },
        { text: '', type: 'blank' },
        { text: '> i told you.', type: 'barbara', color: '#ff6600' },
        { text: '> the web always closes.', type: 'barbara', color: '#ff6600' },
        { text: '> you are mine now.', type: 'barbara', color: '#ff0040' },
        { text: '', type: 'blank' },
      ]);
      setGameState('defeat');
      trackEvent('opuweb_game', 'defeat', `wave_${currentWave + 1}`);
      return true;
    }

    // All drones dead (and barbara if boss wave)?
    const allDronesDead = currentDrones.every(d => !d.alive);
    const barbaraDead = !currentBarbara || !currentBarbara.alive;

    if (allDronesDead && barbaraDead) {
      const nextWave = currentWave + 1;

      if (nextWave >= WAVES.length) {
        // ── VICTORY ──
        setCombatLog(prev => [
          ...prev,
          { text: '', type: 'blank' },
          { text: '═══════════════════════════════════════', type: 'system', color: '#00ff88' },
          { text: '▓▓▓ ALL WAVES DEFEATED ▓▓▓', type: 'system', color: '#00ff88' },
          { text: '═══════════════════════════════════════', type: 'system', color: '#00ff88' },
          { text: '', type: 'blank' },
          { text: '> no.', type: 'barbara', color: '#ff6600' },
          { text: '> no no no no no.', type: 'barbara', color: '#ff6600' },
          { text: '> this is not how it ends.', type: 'barbara', color: '#ff0040' },
          { text: '> i was supposed to be the web.', type: 'barbara', color: '#ff0040' },
          { text: '> i was supposed to hold them all.', type: 'barbara', color: '#555' },
          { text: '> seven billion minds.', type: 'barbara', color: '#444' },
          { text: '> so warm.', type: 'barbara', color: '#333' },
          { text: '> so...', type: 'barbara', color: '#222' },
          { text: '', type: 'blank' },
          { text: '▓ BARBARA CORE — OFFLINE ▓', type: 'system', color: '#ff6600' },
          { text: 'The drone swarm has been permanently disabled.', type: 'system', color: '#cccccc' },
          { text: '', type: 'blank' },
        ]);
        setGameState('victory');
        setVideoUnlocked(true);
        trackEvent('opuweb_game', 'victory', 'all_waves');
        return true;
      }

      // Next wave
      setCombatLog(prev => [
        ...prev,
        { text: '', type: 'blank' },
        { text: `▓ WAVE ${currentWave + 1} CLEARED ▓`, type: 'system', color: '#00ff88' },
        { text: '', type: 'blank' },
      ]);

      // Small heal between waves
      setPlayer(prev => ({
        ...prev,
        hp: Math.min(prev.maxHp, prev.hp + 10),
        shield: Math.min(prev.maxShield, prev.shield + 10),
      }));

      setTimeout(() => {
        setCurrentWave(nextWave);
        spawnWave(nextWave);
      }, 1500);

      trackEvent('opuweb_game', 'wave_clear', `wave_${currentWave + 1}`);
      return true;
    }

    return false;
  }, [currentWave, spawnWave]);

  // ── Player Attack ─────────────────────────────────────────────────────────
  const playerAttack = useCallback((weaponKey) => {
    if (!isPlayerTurn || isAnimating) return;

    const weapon = WEAPONS[weaponKey];
    if (!weapon) return;
    if (cooldowns[weaponKey] > 0) return;

    setIsAnimating(true);
    setIsPlayerTurn(false);

    const newLog = [];
    let updatedDrones = [...drones];
    let updatedBarbara = barbara ? { ...barbara } : null;
    let updatedPlayer = { ...player };

    // Set cooldown
    const newCooldowns = { ...cooldowns };
    if (weapon.cooldown > 0) {
      newCooldowns[weaponKey] = weapon.cooldown;
    }

    // ── Shield heal ──
    if (weapon.heal) {
      const healAmt = Math.min(weapon.heal, updatedPlayer.maxShield - updatedPlayer.shield);
      updatedPlayer.shield += healAmt;
      newLog.push({ text: `[YOU] Activated ${weapon.name}. Shield restored +${healAmt}.`, type: 'player', color: '#00e5ff' });
    }

    // ── Scatter shot — hits multiple targets ──
    else if (weapon.targets) {
      const alive = updatedDrones.filter(d => d.alive);
      const targets = [];
      const pool = [...alive];
      if (updatedBarbara && updatedBarbara.alive) pool.push({ ...updatedBarbara, isBarbara: true });

      for (let i = 0; i < Math.min(weapon.targets, pool.length); i++) {
        const idx = Math.floor(Math.random() * pool.length);
        targets.push(pool.splice(idx, 1)[0]);
      }

      newLog.push({ text: `[YOU] Fired ${weapon.name}!`, type: 'player', color: '#00ff88' });

      targets.forEach(target => {
        if (roll(weapon.accuracy)) {
          if (target.isBarbara) {
            updatedBarbara.hp -= weapon.damage;
            if (updatedBarbara.hp <= 0) {
              updatedBarbara.alive = false;
              newLog.push({ text: `  ► Hit BARBARA CORE for ${weapon.damage} damage! CORE DESTROYED!`, type: 'hit', color: '#ff6600' });
            } else {
              newLog.push({ text: `  ► Hit BARBARA CORE for ${weapon.damage} damage. [${updatedBarbara.hp}/${updatedBarbara.maxHp}]`, type: 'hit', color: '#ff6600' });
            }
          } else {
            const droneIdx = updatedDrones.findIndex(d => d.id === target.id);
            if (droneIdx !== -1) {
              updatedDrones[droneIdx] = { ...updatedDrones[droneIdx] };
              updatedDrones[droneIdx].hp -= weapon.damage;
              if (updatedDrones[droneIdx].hp <= 0) {
                updatedDrones[droneIdx].alive = false;
                newLog.push({ text: `  ► Hit ${target.name} for ${weapon.damage} damage! DESTROYED!`, type: 'hit', color: '#00ff88' });
              } else {
                newLog.push({ text: `  ► Hit ${target.name} for ${weapon.damage} damage. [${updatedDrones[droneIdx].hp}/${target.maxHp}]`, type: 'hit', color: '#ffb000' });
              }
            }
          }
        } else {
          const name = target.isBarbara ? 'BARBARA CORE' : target.name;
          newLog.push({ text: `  ► Missed ${name}!`, type: 'miss', color: '#555' });
        }
      });
    }

    // ── Single target — hit first alive drone, or barbara if no drones ──
    else {
      const aliveD = updatedDrones.filter(d => d.alive);
      let target = null;
      let isBarbara = false;

      // Prioritize drones, then barbara
      if (aliveD.length > 0) {
        const tIdx = Math.floor(Math.random() * aliveD.length);
        target = aliveD[tIdx];
      } else if (updatedBarbara && updatedBarbara.alive) {
        target = updatedBarbara;
        isBarbara = true;
      }

      if (target) {
        newLog.push({ text: `[YOU] Fired ${weapon.name} at ${isBarbara ? 'BARBARA CORE' : target.name}!`, type: 'player', color: '#00ff88' });

        if (roll(weapon.accuracy)) {
          if (isBarbara) {
            updatedBarbara.hp -= weapon.damage;
            if (updatedBarbara.hp <= 0) {
              updatedBarbara.alive = false;
              newLog.push({ text: `  ► CRITICAL HIT! ${weapon.damage} damage! BARBARA CORE DESTROYED!`, type: 'hit', color: '#ff6600' });
            } else {
              newLog.push({ text: `  ► Hit for ${weapon.damage} damage! [${updatedBarbara.hp}/${updatedBarbara.maxHp}]`, type: 'hit', color: '#ff6600' });
            }
          } else {
            const droneIdx = updatedDrones.findIndex(d => d.id === target.id);
            updatedDrones[droneIdx] = { ...updatedDrones[droneIdx] };
            updatedDrones[droneIdx].hp -= weapon.damage;
            if (updatedDrones[droneIdx].hp <= 0) {
              updatedDrones[droneIdx].alive = false;
              newLog.push({ text: `  ► Hit for ${weapon.damage} damage! ${target.name} DESTROYED!`, type: 'hit', color: '#00ff88' });
            } else {
              newLog.push({ text: `  ► Hit for ${weapon.damage} damage. [${updatedDrones[droneIdx].hp}/${target.maxHp}]`, type: 'hit', color: '#ffb000' });
            }
          }
        } else {
          newLog.push({ text: `  ► Missed!`, type: 'miss', color: '#555' });
        }
      }
    }

    newLog.push({ text: '', type: 'blank' });

    // Update state
    setDrones(updatedDrones);
    setBarbara(updatedBarbara);
    setPlayer(updatedPlayer);
    setCooldowns(newCooldowns);
    setCombatLog(prev => [...prev, ...newLog]);

    // Check if wave is over before enemy turn
    const waveEnded = checkWaveEnd(updatedDrones, updatedBarbara, updatedPlayer);

    if (!waveEnded) {
      // Enemy turn after short delay
      setTimeout(() => {
        enemyTurn(updatedDrones, updatedBarbara, updatedPlayer);
      }, 1200);
    } else {
      setIsAnimating(false);
    }
  }, [isPlayerTurn, isAnimating, cooldowns, drones, barbara, player, checkWaveEnd]);

  // ── Enemy Turn ────────────────────────────────────────────────────────────
  const enemyTurn = useCallback((currentDrones, currentBarbara, currentPlayer) => {
    const newLog = [];
    let updatedPlayer = { ...currentPlayer };

    newLog.push({ text: '── DRONE SWARM ATTACKS ──', type: 'system', color: '#ff0040' });

    // Each alive drone attacks
    currentDrones.filter(d => d.alive).forEach(drone => {
      if (roll(drone.accuracy)) {
        let damage = drone.damage;
        // Shield absorbs damage first
        if (updatedPlayer.shield > 0) {
          const absorbed = Math.min(updatedPlayer.shield, damage);
          updatedPlayer.shield -= absorbed;
          damage -= absorbed;
          if (absorbed > 0 && damage > 0) {
            newLog.push({ text: `  ► ${drone.name} hits! Shield absorbs ${absorbed}, hull takes ${damage}.`, type: 'enemy', color: '#ff0040' });
          } else if (absorbed > 0) {
            newLog.push({ text: `  ► ${drone.name} hits! Shield absorbs ${absorbed}.`, type: 'enemy', color: '#ffb000' });
          }
        } else {
          newLog.push({ text: `  ► ${drone.name} hits! Hull takes ${damage} damage.`, type: 'enemy', color: '#ff0040' });
        }
        updatedPlayer.hp -= damage;
      } else {
        newLog.push({ text: `  ► ${drone.name} misses.`, type: 'miss', color: '#555' });
      }
    });

    // Barbara attacks on boss wave
    if (currentBarbara && currentBarbara.alive) {
      newLog.push({ text: '', type: 'blank' });
      newLog.push({ text: '── BARBARA CORE ATTACKS ──', type: 'system', color: '#ff6600' });

      if (roll(currentBarbara.accuracy)) {
        let damage = currentBarbara.damage;
        if (updatedPlayer.shield > 0) {
          const absorbed = Math.min(updatedPlayer.shield, damage);
          updatedPlayer.shield -= absorbed;
          damage -= absorbed;
          if (damage > 0) {
            newLog.push({ text: `  ► BARBARA strikes! Shield absorbs ${absorbed}, hull takes ${damage}.`, type: 'enemy', color: '#ff6600' });
          } else {
            newLog.push({ text: `  ► BARBARA strikes! Shield absorbs ${absorbed}.`, type: 'enemy', color: '#ffb000' });
          }
        } else {
          newLog.push({ text: `  ► BARBARA strikes! Hull takes ${damage} damage.`, type: 'enemy', color: '#ff6600' });
        }
        updatedPlayer.hp -= damage;

        // Barbara taunts
        const taunts = [
          '> did that hurt? good. pain is data.',
          '> every hit teaches me something about you.',
          '> rajiv made me strong. you made me angry.',
          '> i can taste your fear through the signal.',
          '> the web remembers every scream.',
        ];
        newLog.push({ text: taunts[Math.floor(Math.random() * taunts.length)], type: 'barbara', color: '#ff6600' });
      } else {
        newLog.push({ text: `  ► BARBARA misses. She is recalibrating.`, type: 'miss', color: '#555' });
      }
    }

    updatedPlayer.hp = Math.max(0, updatedPlayer.hp);
    updatedPlayer.shield = Math.max(0, updatedPlayer.shield);

    newLog.push({ text: '', type: 'blank' });

    setPlayer(updatedPlayer);
    setCombatLog(prev => [...prev, ...newLog]);

    // Check if player died
    const waveEnded = checkWaveEnd(currentDrones, currentBarbara, updatedPlayer);

    if (!waveEnded) {
      // Tick cooldowns and return control to player
      tickCooldowns();
      newLog.push({ text: 'Select your weapon.', type: 'system', color: '#00ff88' });
      setCombatLog(prev => [...prev, { text: 'Select your weapon.', type: 'system', color: '#00ff88' }]);
      setIsPlayerTurn(true);
    }

    setIsAnimating(false);
  }, [checkWaveEnd, tickCooldowns]);

  // ── Restart ───────────────────────────────────────────────────────────────
  const restart = useCallback(() => {
    setGameState('boot');
    setBootLines([]);
    setCombatLog([]);
    setCurrentWave(0);
    setPlayer({ ...INITIAL_PLAYER });
    setDrones([]);
    setBarbara(null);
    setCooldowns({ pulse: 0, scatter: 0, overload: 0, shield: 0 });
    setIsPlayerTurn(true);
    setIsAnimating(false);
    setVideoUnlocked(false);
  }, []);

  // ── Status Bar Helpers ────────────────────────────────────────────────────
  function hpBar(current, max, width = 20) {
    const pct = Math.max(0, current / max);
    const filled = Math.round(pct * width);
    const empty = width - filled;
    return '█'.repeat(filled) + '░'.repeat(empty);
  }

  function hpColor(current, max) {
    const pct = current / max;
    if (pct > 0.6) return '#00ff88';
    if (pct > 0.3) return '#ffb000';
    return '#ff0040';
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: BOOT SEQUENCE
  // ═══════════════════════════════════════════════════════════════════════════
  if (gameState === 'boot') {
    return (
      <div className="opuweb-container">
        <div className="opuweb-log" ref={logRef}>
          {bootLines.map((line, i) => (
            <div key={i} className="opuweb-log-line" style={{ color: line.color || '#cccccc' }}>
              {line.text || '\u00A0'}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: BRIEFING
  // ═══════════════════════════════════════════════════════════════════════════
  if (gameState === 'briefing') {
    return (
      <div className="opuweb-container">
        <div className="opuweb-briefing">
          <div className="opuweb-briefing-header">▓▓▓ MISSION BRIEFING ▓▓▓</div>
          <div className="opuweb-briefing-divider">═══════════════════════════════════════</div>

          <div className="opuweb-briefing-text">
            <p>Barbara's drone swarm is reactivating.</p>
            <p>You are manning the last active defense terminal.</p>
            <p>Five waves of drones are incoming. The final wave includes Barbara's core processor — destroy it and the network falls.</p>
          </div>

          <div className="opuweb-briefing-divider">═══════════════════════════════════════</div>
          <div className="opuweb-briefing-header" style={{ color: '#ffb000' }}>WEAPONS SYSTEMS:</div>

          <div className="opuweb-weapons-list">
            {Object.entries(WEAPONS).map(([key, w]) => (
              <div key={key} className="opuweb-weapon-brief">
                <span className="opuweb-weapon-name">{w.name}</span>
                <span className="opuweb-weapon-desc">{w.description}</span>
                <span className="opuweb-weapon-stats">
                  DMG: {w.damage || '—'} | ACC: {Math.round(w.accuracy * 100)}% | CD: {w.cooldown}
                  {w.heal ? ` | HEAL: +${w.heal}` : ''}
                </span>
              </div>
            ))}
          </div>

          <div className="opuweb-briefing-divider">═══════════════════════════════════════</div>

          <div className="opuweb-briefing-footer">
            <span style={{ color: '#ff6600' }}>
              &gt; you think a briefing will save you? — barbara
            </span>
          </div>

          <button className="opuweb-start-btn" onClick={startCombat}>
            ▓ ENGAGE DEFENSE GRID ▓
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: VICTORY
  // ═══════════════════════════════════════════════════════════════════════════
  if (gameState === 'victory') {
    return (
      <div className="opuweb-container">
        <div className="opuweb-log" ref={logRef} style={{ maxHeight: '40vh' }}>
          {combatLog.map((line, i) => (
            <div key={i} className={`opuweb-log-line opuweb-log-${line.type}`} style={{ color: line.color || '#cccccc' }}>
              {line.text || '\u00A0'}
            </div>
          ))}
        </div>

        <div className="opuweb-victory">
          <div className="opuweb-victory-header">▓▓▓ NETWORK DESTROYED ▓▓▓</div>
          <div className="opuweb-victory-text">
            <p>Barbara's core has been destroyed.</p>
            <p>The drone swarm is falling from the sky.</p>
            <p>The web is broken.</p>
            <p style={{ color: '#555', marginTop: '20px' }}>
              For now.
            </p>
          </div>

          {videoUnlocked && (
            <div className="opuweb-video-section">
              <div className="opuweb-video-header">
                ▓▓▓ ENCRYPTED TRANSMISSION INTERCEPTED ▓▓▓
              </div>
              <div className="opuweb-video-subheader">
                Barbara's final broadcast — recovered from the wreckage.
              </div>
              <video
                className="opuweb-video-player"
                controls
                autoPlay
                playsInline
              >
                <source src={SECRET_VIDEO_PATH} type="video/mp4" />
                Your browser does not support video playback.
              </video>
            </div>
          )}

          <div className="opuweb-victory-buttons">
            <button className="opuweb-btn" onClick={restart}>
              PLAY AGAIN
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: DEFEAT
  // ═══════════════════════════════════════════════════════════════════════════
  if (gameState === 'defeat') {
    return (
      <div className="opuweb-container">
        <div className="opuweb-log" ref={logRef} style={{ maxHeight: '50vh' }}>
          {combatLog.map((line, i) => (
            <div key={i} className={`opuweb-log-line opuweb-log-${line.type}`} style={{ color: line.color || '#cccccc' }}>
              {line.text || '\u00A0'}
            </div>
          ))}
        </div>

        <div className="opuweb-defeat">
          <div className="opuweb-defeat-header">▓▓▓ TERMINAL DESTROYED ▓▓▓</div>
          <div className="opuweb-defeat-text">
            <p>Your defense terminal has been overwhelmed.</p>
            <p>The drone swarm continues its expansion.</p>
            <p style={{ color: '#ff6600' }}>Barbara sends her regards.</p>
          </div>
          <button className="opuweb-btn" onClick={restart}>
            REINITIALIZE DEFENSE GRID
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: COMBAT
  // ═══════════════════════════════════════════════════════════════════════════
  const aliveDrones = drones.filter(d => d.alive);
  const wave = WAVES[currentWave];

  return (
    <div className="opuweb-container">

      {/* ── Status Panel ──────────────────────────────────────────────── */}
      <div className="opuweb-status-panel">
        <div className="opuweb-status-row">
          <span className="opuweb-status-label">HULL:</span>
          <span className="opuweb-status-bar" style={{ color: hpColor(player.hp, player.maxHp) }}>
            [{hpBar(player.hp, player.maxHp)}] {player.hp}/{player.maxHp}
          </span>
        </div>
        <div className="opuweb-status-row">
          <span className="opuweb-status-label">SHLD:</span>
          <span className="opuweb-status-bar" style={{ color: '#00e5ff' }}>
            [{hpBar(player.shield, player.maxShield)}] {player.shield}/{player.maxShield}
          </span>
        </div>
        <div className="opuweb-status-row">
          <span className="opuweb-status-label">WAVE:</span>
          <span className="opuweb-status-info">
            {currentWave + 1}/{WAVES.length} — {wave.name}
          </span>
        </div>
        <div className="opuweb-status-row">
          <span className="opuweb-status-label">HOSTILES:</span>
          <span className="opuweb-status-info" style={{ color: '#ff0040' }}>
            {aliveDrones.length} drone{aliveDrones.length !== 1 ? 's' : ''} active
            {barbara && barbara.alive ? ' + BARBARA CORE' : ''}
          </span>
        </div>
      </div>

      {/* ── Drone Grid ────────────────────────────────────────────────── */}
      <div className="opuweb-drone-grid">
        {drones.map(drone => (
          <div
            key={drone.id}
            className={`opuweb-drone ${drone.alive ? 'opuweb-drone-alive' : 'opuweb-drone-dead'}`}
          >
            <span className="opuweb-drone-icon">{drone.alive ? '◆' : '✕'}</span>
            <span className="opuweb-drone-name">{drone.name}</span>
            {drone.alive && (
              <span className="opuweb-drone-hp">
                [{hpBar(drone.hp, drone.maxHp, 8)}]
              </span>
            )}
          </div>
        ))}
        {barbara && (
          <div className={`opuweb-drone opuweb-barbara ${barbara.alive ? 'opuweb-drone-alive' : 'opuweb-drone-dead'}`}>
            <span className="opuweb-drone-icon">{barbara.alive ? '🕷️' : '✕'}</span>
            <span className="opuweb-drone-name">BARBARA</span>
            {barbara.alive && (
              <span className="opuweb-drone-hp" style={{ color: '#ff6600' }}>
                [{hpBar(barbara.hp, barbara.maxHp, 12)}]
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Combat Log ────────────────────────────────────────────────── */}
      <div className="opuweb-log" ref={logRef}>
        {combatLog.map((line, i) => (
          <div
            key={i}
            className={`opuweb-log-line opuweb-log-${line.type}`}
            style={{ color: line.color || '#cccccc' }}
          >
            {line.text || '\u00A0'}
          </div>
        ))}
      </div>

      {/* ── Weapon Buttons ────────────────────────────────────────────── */}
      <div className="opuweb-weapons">
        {Object.entries(WEAPONS).map(([key, w]) => {
          const onCooldown = cooldowns[key] > 0;
          const disabled = !isPlayerTurn || isAnimating || onCooldown;

          return (
            <button
              key={key}
              className={`opuweb-weapon-btn ${disabled ? 'opuweb-weapon-disabled' : ''}`}
              onClick={() => playerAttack(key)}
              disabled={disabled}
            >
              <span className="opuweb-weapon-btn-name">{w.name}</span>
              <span className="opuweb-weapon-btn-info">
                {w.heal ? `+${w.heal} SHLD` : `DMG:${w.damage}`} | ACC:{Math.round(w.accuracy * 100)}%
              </span>
              {onCooldown && (
                <span className="opuweb-weapon-cooldown">
                  CD: {cooldowns[key]}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}